import { useMutation, useQuery } from '@apollo/client'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
} from '@mui/material'
import { useState } from 'react'
import { PinCandidateNote } from 'recruiter/graphql/candidateNotes.gql'
import { useUrlState, codec } from 'shared/hooks/useUrlState'
import useToast from 'shared/components/Toast/useToast'

import { PinCandidateGroupNote } from 'recruiter/graphql/candidateGroupNotes.gql'
import { DeleteCandidateGroupNote } from 'recruiter/graphql/candidateGroupNotes.gql'
import type { CandidateNote as CandidateNoteType } from 'recruiter/graphql/candidateNotes.gql'
import {
    DeleteCandidateNote,
    GetCandidateNoteTemplates,
} from 'recruiter/graphql/candidateNotes.gql'
import { useNotesContext } from 'shared/utils/noteContext'
import { stripHtmlTags } from 'shared/utils/stripTags'
import NoteDetailPanel from './NoteDetailPanel'
import NoteListPanel from './NoteListPanel'
import NotesFilterBar from './NotesFilterBar'
import NotesToolbar, { SORT_ORDER } from './NotesToolbar'

const urlCodec = {
    note: codec.string(),
}

const Notes = ({ data, refetch }) => {
    const [pinCandidateNote] = useMutation(PinCandidateNote)
    const [pinCandidateGroupNote] = useMutation(PinCandidateGroupNote)
    const [deleteCandidateNote] = useMutation(DeleteCandidateNote)
    const [deleteCandidateGroupNote] = useMutation(DeleteCandidateGroupNote)
    const { data: noteTemplates } = useQuery(GetCandidateNoteTemplates)
    const toast = useToast()

    const { state: urlState, setState: setUrlState } = useUrlState(urlCodec)
    const [sortOrder, setSortOrder] = useState<SORT_ORDER | null>(null)
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const { openEditNote } = useNotesContext()

    // Data pipeline
    const allNotes: CandidateNoteType[] = data?.candidate?.notes ?? []
    const activeNotes = allNotes.filter((n) => n.isActive)
    const pinnedNotes = activeNotes.filter((n) => n.isPinned)

    // Filter by template type
    let visibleNotes = [...activeNotes]
    if (selectedFilter) {
        visibleNotes = visibleNotes.filter(
            (note) => note.template?.templateType === selectedFilter
        )
    }

    // Filter by search
    if (searchValue) {
        visibleNotes = visibleNotes.filter((note) =>
            note.noteData?.toLowerCase().includes(searchValue.toLowerCase())
        )
    }

    // Sort (default: newest by createdAt)
    if (sortOrder === 'top rated') {
        visibleNotes.sort((a, b) => (b?.starRating || 0) - (a?.starRating || 0))
    } else if (sortOrder === 'oldest') {
        visibleNotes.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
    } else {
        // Default: newest first by createdAt (covers null sortOrder and 'newest')
        visibleNotes.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    }

    visibleNotes.sort((a, b) => {
        const aIsMain = a.template?.templateType === 'Main' ? 1 : 0
        const bIsMain = b.template?.templateType === 'Main' ? 1 : 0
        return bIsMain - aIsMain
    })

    const selectedNote =
        visibleNotes.find((n) => n.noteId === urlState.note) ??
        visibleNotes[0] ??
        null

    // canDelete derivation
    const canDeleteTypes =
        noteTemplates?.noteTemplates
            .filter((t) => t.canDelete)
            .map((t) => t.templateType) ?? []
    const canDeleteSelected = selectedNote?.template?.templateType
        ? canDeleteTypes.includes(selectedNote.template.templateType)
        : false

    // Templates list for filter bar
    const templates =
        noteTemplates?.noteTemplates.map((t) => ({
            id: t.id,
            templateType: t.templateType,
        })) ?? []

    // Handlers
    const pinNote = (note: CandidateNoteType) => async () => {
        if (note.noteType === 'group') {
            await pinCandidateGroupNote({
                variables: { noteId: note.noteId || '' },
                onCompleted: (data) => {
                    if (data.toggleCandidateGroupNotePin?.success) {
                        refetch()
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.toggleCandidateGroupNotePin.message ??
                                'Failed to toggle group note pin.',
                            severity: 'error',
                        })
                    }
                },
                onError: (error) => {
                    console.error(error)
                    toast({
                        title: 'Error!',
                        message:
                            'Something went wrong. Please try again later.',
                        severity: 'error',
                    })
                },
            })
        } else {
            await pinCandidateNote({
                variables: { noteId: note.noteId || '' },
                onCompleted: (data) => {
                    if (data.toggleCandidateNotePin?.success) {
                        refetch()
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.toggleCandidateNotePin.message ??
                                'Failed to toggle note pin.',
                            severity: 'error',
                        })
                    }
                },
                onError: (error) => {
                    console.error(error)
                    toast({
                        title: 'Error!',
                        message:
                            'Something went wrong. Please try again later.',
                        severity: 'error',
                    })
                },
            })
        }
    }

    const handleCopy = async () => {
        if (!selectedNote?.noteData) return
        try {
            await navigator.clipboard.writeText(
                stripHtmlTags(selectedNote.noteData)
            )
            toast({
                title: 'Copied',
                message: 'Note content copied to clipboard.',
                severity: 'success',
            })
        } catch {
            toast({
                title: 'Error',
                message: 'Failed to copy note content.',
                severity: 'error',
            })
        }
    }

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(true)
    }

    const handleDeleteNote = async () => {
        if (!selectedNote) return
        setDeleteConfirmOpen(false)

        const handleMutationError = (error) => {
            console.error(error)
            toast({
                title: 'Error!',
                message: 'Something went wrong. Please try again later.',
                severity: 'error',
            })
        }

        if (selectedNote.noteType === 'group') {
            await deleteCandidateGroupNote({
                variables: { noteId: selectedNote.noteId || '' },
                onCompleted: (data) => {
                    if (data.deleteCandidateGroupNote.success) {
                        // Select next note or null
                        const currentIndex = visibleNotes.findIndex(
                            (n) => n.noteId === selectedNote.noteId
                        )
                        const nextNote =
                            visibleNotes[currentIndex + 1] ||
                            visibleNotes[currentIndex - 1]
                        setUrlState({ note: nextNote?.noteId ?? undefined })
                        refetch()
                        toast({
                            title: 'Success',
                            message: 'Group note deleted successfully.',
                            severity: 'success',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.deleteCandidateGroupNote.message ??
                                'Failed to delete group note.',
                            severity: 'error',
                        })
                    }
                },
                onError: handleMutationError,
            })
        } else {
            await deleteCandidateNote({
                variables: { noteId: selectedNote.noteId || '' },
                onCompleted: (data) => {
                    if (data.deleteCandidateNote.success) {
                        const currentIndex = visibleNotes.findIndex(
                            (n) => n.noteId === selectedNote.noteId
                        )
                        const nextNote =
                            visibleNotes[currentIndex + 1] ||
                            visibleNotes[currentIndex - 1]
                        setUrlState({ note: nextNote?.noteId ?? undefined })
                        refetch()
                        toast({
                            title: 'Success',
                            message: 'Note deleted successfully.',
                            severity: 'success',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.deleteCandidateNote.message ??
                                'Failed to delete note.',
                            severity: 'error',
                        })
                    }
                },
                onError: handleMutationError,
            })
        }
    }

    return (
        <>
            <Paper
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 280px)',
                    minHeight: 500,
                }}
            >
                <NotesToolbar
                    selectedFilter={selectedFilter}
                    sortOrder={sortOrder}
                    searchValue={searchValue}
                    pinnedNotes={pinnedNotes}
                    onFilterChange={setSelectedFilter}
                    onSortChange={setSortOrder}
                    onSearchChange={setSearchValue}
                    onAddNote={() => openEditNote(null)}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters((prev) => !prev)}
                    notesCount={visibleNotes.length}
                    onSelectNote={(noteId) => setUrlState({ note: noteId })}
                />
                <NotesFilterBar
                    visible={showFilters}
                    templates={templates}
                    selectedFilter={selectedFilter}
                    onFilterChange={setSelectedFilter}
                />
                <Divider />
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    <NoteListPanel
                        notes={visibleNotes}
                        selectedNoteId={selectedNote?.noteId ?? null}
                        onSelectNote={(note) =>
                            setUrlState({ note: note.noteId ?? undefined })
                        }
                    />
                    <NoteDetailPanel
                        note={selectedNote}
                        onPin={selectedNote ? pinNote(selectedNote) : () => {}}
                        onEdit={() =>
                            selectedNote && openEditNote(selectedNote)
                        }
                        onDelete={
                            canDeleteSelected ? handleDeleteConfirm : undefined
                        }
                        onCopy={handleCopy}
                    />
                </Box>
            </Paper>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Delete Note</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this note? This action
                        cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteNote}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Notes
